import cloneDeep from 'lodash/cloneDeep';
import { Typography } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import ClearIcon from '@material-ui/icons/Clear';
import ErrorIcon from '@material-ui/icons/Error';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import PropTypes from 'prop-types';
import React from 'react';
import XLSX from 'xlsx';
import * as cptable from 'xlsx/dist/cpexcel.full.mjs';
import IRIgeneration from '../../core/IRIgeneration';
import LiteralConfirmationField from '../../core/LiteralConfirmationField';
import SlashHash from '../../core/SlashHash';
import TitledPanel from '../../core/TitledPanel';
import GS from '../../grafsync/grafsync';
import { SKOS_DATATYPE_CONFIGURATION } from '../../vocabulary/constants';
import LabelledSwitch from '../LabelledSwitch';
import Loading from '../Loading';
import MenuSelector from '../MenuSelector';
import Notification from '../Notification';
import BaseIRI from '../project/BaseIRI';
import ProjectLanguageSelector from '../ProjectLanguageSelector';
import { globalStyles as styles } from '../style/gstheme';
import TitledTextField from '../TitledTextField';
import Upgrade from '../Upgrade';
import { isRTL, sanitiseText } from '../../core/utils/utils';
import { MAX_TITLE_LENGTH } from '../utils/constants';

XLSX.set_cptable(cptable);

const CONFIG_OPTIONS = {
    "taxonomy": {
        componentName: "taxonomy",
        unsuitableDomains: [
            'http://www.w3.org/2008/05/skos-xl#Label',
            'http://www.w3.org/2004/02/skos/core#Collection',
            'http://www.w3.org/2004/02/skos/core#OrderedCollection',
            'http://www.w3.org/2004/02/skos/core#ConceptScheme'
        ],
        labelProp: "http://www.w3.org/2004/02/skos/core#prefLabel",
        options: {
            datatypeProperties: SKOS_DATATYPE_CONFIGURATION,
            objectProperties: {
                "related": {
                    "displayName": "Related",
                    "xref": true,
                    "iri": "http://www.w3.org/2004/02/skos/core#related"
                },
                "closeMatch": {
                    "displayName": "Close Match",
                    "xref": true,
                    "xrefOtherComponents": true,
                    "iri": "http://www.w3.org/2004/02/skos/core#closeMatch"
                },
                "exactMatch": {
                    "displayName": "Exact Match",
                    "xref": true,
                    "xrefOtherComponents": true,
                    "iri": "http://www.w3.org/2004/02/skos/core#exactMatch"
                },
                "relatedMatch": {
                    "displayName": "Related Match",
                    "xref": true,
                    "xrefOtherComponents": true,
                    "iri": "http://www.w3.org/2004/02/skos/core#relatedMatch"
                },
                "type": {
                    "displayName": "Class",
                    "lang": true,
                    "iri": "http://www.w3.org/1999/02/22-rdf-syntax-ns#type"
                }
            }
        },
        xrefOptions: [
            {
                value: "http://www.w3.org/2004/02/skos/core#prefLabel",
                label: "Preferred Label"
            }
        ]
    },
    "dataset": {
        componentName: "dataset",
        unsuitableDomains: [
            'http://www.w3.org/2008/05/skos-xl#Label',
            'http://www.w3.org/2004/02/skos/core#Collection',
            'http://www.w3.org/2004/02/skos/core#OrderedCollection',
            'http://www.w3.org/2004/02/skos/core#ConceptScheme',
            'http://www.w3.org/2004/02/skos/core#Concept'
        ],
        labelProp: "http://www.w3.org/2000/01/rdf-schema#label",
        singleLabel: true,
        options: {
            datatypeProperties: {
                "prefLabel": {
                    "displayName": "Label",
                    "iri": "http://www.w3.org/2000/01/rdf-schema#label",
                    "lang": true
                }
            },
            objectProperties: {
                "type": {
                    "displayName": "Class",
                    "lang": true,
                    "iri": "http://www.w3.org/1999/02/22-rdf-syntax-ns#type"
                }
            }
        },
        xrefOptions: [
            {
                value: "http://www.w3.org/2000/01/rdf-schema#label",
                label: "Label"
            }
        ]
    }
};

const ROW_PREVIEW_COUNTS = [
    {
        value: "5",
        label: "5"
    },
    {
        value: "10",
        label: "10"
    },
    {
        value: "25",
        label: "25"
    },
    {
        value: "50",
        label: "50"
    },
    {
        value: "100",
        label: "100"
    }
];

const ROWS = 100;
const MAX_ROWS = 100000;
const MAX_COLS = 100;
const USE_IRI = "https://grafsync.graphifi.com/schema/tsv/IRI";

class SheetPreview extends React.Component {

    constructor(props) {
        super(props);
        let maxRows = 0;
        if (props.limits) {
            const subscriptionOptions = GS.subscriptionOptions();
            const subscriptionType = GS.getActiveSubscriptionType();
            const importOptions = subscriptionOptions.find(option => option.subscriptionType === subscriptionType).import[props.limits];
            maxRows = importOptions.rows;
        }

        const xrefComponents = props.xrefComponents ?
            this.props.xrefComponents
                .map(t => ({
                    value: t.id,
                    component: true,
                    label: "From " + CONFIG_OPTIONS[props.configOptions].componentName + ": " + (t.title && t.title[props.defaultLang] ? t.title[props.defaultLang] : t.id)
                }))
            :
            [];
        const additionalOptions = props.loadInfo ?
            props.loadInfo.map(item => ({
                value: item.iri,
                isTemplateProp: item.isTemplateProp,
                label: item.label ?
                    item.label[props.defaultLang] ?
                        item.label[props.defaultLang]
                        :
                        item.label["und"] ?
                            item.label["und"]
                            :
                            item.iri
                    :
                    item.iri,
                lang: item.range && item.range.includes("http://www.w3.org/1999/02/22-rdf-syntax-ns#langString"),
                range: item.range,
                xref: item.type && item.type.includes("ObjectProperty")
            })).sort((a, b) => {
                const sortA = a.label || a.iri;
                const sortB = b.label || b.iri;
                if (sortA < sortB) return -1;
                return 1;
            })
            :
            [];
        const menuOptions = this.computeMenuOptions(props.configOptions, props.byIRI);
        const initialState = {
            menuOptions,
            ss: null,
            isLoading: true,
            maxRows: maxRows > 0 ? maxRows : props.maxRows,
            baseIRI: "",
            lang: props.initialSettings.defaultLang || props.defaultLang,
            isValidIRI: false,
            additionalOptions,
            showRows: "5",
            primaryCounter: 1,
            secondaryCounter: 10000000,
            rowsCount: 0,
            columnsCount: 0,
            emptyCols: [],
            xrefComponents,
            properties: [],
            showAdvancedOptions: false,
            title: props.initialSettings.title || "",
            errors: [],
            codepage: props.initialSettings.codepage || undefined,
            ignoreHeaderRow: props.initialSettings.ignoreHeaderRow || false,
            ignoreInvalidValues: props.initialSettings.ignoreInvalidValues || false,
            useXL: props.initialSettings.useXL || false,
            removeSpaces: props.initialSettings.removeSpaces || false,
            replaceSpaces: props.initialSettings.replaceSpaces || '',
            removeSpecialCharacters: props.initialSettings.removeSpecialCharacters || false,
            replaceSpecialCharacters: props.initialSettings.replaceSpecialCharacters || '',
            minimiseEncoding: props.initialSettings.minimiseEncoding === "false" ? false : true,
            prependText: "prependText" in props.initialSettings && props.initialSettings.prependText !== undefined ?
                props.initialSettings.prependText
                :
                "label",
            appendText: "appendText" in props.initialSettings && props.initialSettings.appendText !== undefined ?
                props.initialSettings.appendText
                :
                props.defaultLang,
            appendLang: props.initialSettings.appendText === "" ? false : true,
            prependLabel: "prependText" in props.initialSettings && props.initialSettings.prependText === "" ? false : true
        };
        this.state = initialState;
    }

    computeMenuOptions(configOptions, allowMergeByIRI) {
        const options = CONFIG_OPTIONS[configOptions].options;
        const objectProperties = Object.keys(options.objectProperties);
        const datatypeProperties = Object.keys(options.datatypeProperties);

        const dataTypeOptions = datatypeProperties
            .filter(prop => !options.datatypeProperties[prop].excludeFromImport)
            .map(prop => ({
                iri: options.datatypeProperties[prop].iri,
                label: options.datatypeProperties[prop].displayName,
                lang: options.datatypeProperties[prop].lang
            }));

        const objectOptions = objectProperties.map(prop => ({
            iri: options.objectProperties[prop].iri,
            label: options.objectProperties[prop].displayName,
            xref: options.objectProperties[prop].xref,
            xrefOtherComponents: options.objectProperties[prop].xrefOtherComponents,
            lang: options.objectProperties[prop].lang
        }));

        let optionsList = [
            {
                heading: true,
                label: "Labels and Notes"
            },
            ...dataTypeOptions,
            {
                heading: true,
                label: "Relationships and Classes"
            },
            ...objectOptions,
            {
                heading: true,
                label: "Ontology Properties"
            }
        ];
        if (allowMergeByIRI) {
            optionsList = [
                {
                    iri: USE_IRI,
                    label: "Use IRI"
                },
                ...optionsList
            ];
        }
        const menuOptions = optionsList.map((option) => ({
            value: option.iri,
            label: option.label,
            disabled: option.disabled,
            heading: option.heading,
            ...(option.lang) && { lang: option.lang },
            ...(option.xref) && { xref: option.xref },
            ...(option.xrefOtherComponents) && { xrefOtherComponents: option.xrefOtherComponents },
            used: false
        }));
        return menuOptions;
    }

    componentDidMount() {
        this.processAvailableProps();
        this.loadSpreadsheet();
    }

    async loadSpreadsheet() {
        const name = this.props.file.name;
        if (name.includes('.tsv')) {
            this.setState({ isSpreadsheet: true }, () => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const tsvData = e.target.result;
                    const workbook = XLSX.read(tsvData, {
                        type: 'string',
                        FS: "\t",
                        ...(this.state.codepage) && { codepage: this.state.codepage },
                        sheetRows: this.props.maxRows + 1,
                        raw: true
                    });
                    const sheet = workbook.SheetNames[0];
                    if (!sheet) {
                        this.setState({
                            isLoading: false,
                            errors: ["Cannot read file."]
                        });
                        return;
                    }
                    const arrayData = SheetPreview.convertData(workbook.Sheets[sheet], true, this.props.maxRows);
                    const properties = Array.from({ length: arrayData.columnsCount }, () => ({ property: null }));
                    this.setState({
                        ss: arrayData.ss,
                        isLoading: false,
                        rowsCount: arrayData.rowsCount,
                        columnsCount: arrayData.columnsCount,
                        emptyCols: arrayData.emptyCols,
                        properties: this.props.initialSettings.mapping || properties,
                        errors: arrayData.errors
                    });
                };
                reader.readAsText(this.props.file);
            });
        }
        else if (name.includes('.xlsx') || name.includes('.csv') || name.includes('.txt')) {
            this.setState({ isSpreadsheet: true }, () => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const xlsxData = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(xlsxData, {
                        type: 'array',
                        ...(this.state.codepage) && { codepage: this.state.codepage },
                        sheetRows: this.props.maxRows + 1,
                        raw: true
                    });
                    const sheet = workbook.SheetNames[0];
                    if (!sheet) {
                        this.setState({
                            isLoading: false,
                            errors: ["Cannot read file."]
                        });
                        return;
                    }
                    const arrayData = SheetPreview.convertData(workbook.Sheets[sheet], true, this.props.maxRows);
                    const properties = Array.from({ length: arrayData.columnsCount }, () => ({ property: null }));
                    this.setState({
                        ss: arrayData.ss,
                        isLoading: false,
                        rowsCount: arrayData.rowsCount,
                        columnsCount: arrayData.columnsCount,
                        emptyCols: arrayData.emptyCols,
                        properties: this.props.initialSettings.mapping || properties,
                        errors: arrayData.errors
                    });
                };
                reader.readAsArrayBuffer(this.props.file);
            });
        }
    }

    static convertData(data, isTSV = false, maxRows) {
        if (data["!merges"]) {
            return { errors: [`The document contains merged cells.`] };
        }
        let ss = [];
        const ref = data["!ref"];
        if (ref.indexOf(":") === -1) {
            return { errors: [`The document contains no data.`] };
        }
        const rangeEnd = ref.split(":")[1];
        const rowsCount = parseInt(rangeEnd.replace(/[A-Z]/g, ''));
        if (rowsCount > maxRows) {
            return { errors: [`There are too many rows in the data. The maximum is ${maxRows}.`] };
        }
        const columns = rangeEnd.replace(/[0-9]/g, '');
        const columnsCount = columns.length === 1 ?
            columns.charCodeAt(0) - 64
            :
            (((columns.charCodeAt(0) - 64) * 26) + columns.charCodeAt(1) - 64);
        if (columnsCount > MAX_COLS) {
            return { errors: [`There are too many columns in the data (${columnsCount}). The maximum is ${MAX_COLS}.`] };
        }
        let emptyCols = Array.from({ length: columnsCount }, () => true);
        let nonText = false;
        let nonTextCells = [];
        for (let i = 1; i <= rowsCount; i++) {
            if (i <= ROWS) {
                ss[i] = [];
            }
            for (let j = 0; j < columnsCount; j++) {
                if (i <= ROWS) {
                    ss[i][j] = "";
                }
                const cell = String.fromCharCode(j + 65) + i;
                if (data[cell]) {
                    if (i <= ROWS) {
                        if (!["s", "n", "b"].includes(data[cell].t)) {
                            nonText = true;
                            if (nonTextCells.length < 20) nonTextCells.push(i + ":" + (j + 1));
                            break;
                        }
                        let cellValue = data[cell].w ? data[cell].w.toString() : data[cell].v.toString();
                        if (isTSV) {
                            // We unescape \n for TSV files
                            cellValue = cellValue.replace(/\\n/g, "\n");
                        }
                        // We always remove carriage returns to make sure equivalent between OS systems
                        cellValue = cellValue.replace(/\r/g, "");
                        ss[i][j] = cellValue.trim();
                    }
                    emptyCols[j] = false;
                }
            }
        }
        if (nonText) {
            return {
                errors: [
                    `The document contains content that is not text.`,
                    'This applies to cells: ' + nonTextCells.join(", ")
                ]
            };
        }
        return {
            ss,
            rowsCount,
            columnsCount,
            emptyCols
        };
    }

    handleSelectProperty = (index) => (property) => {
        const newProperties = cloneDeep(this.state.properties);
        newProperties[index].property = property;
        const allOptions = [...this.state.menuOptions, ...this.state.additionalOptions];
        const showLang = property && allOptions.some(opt => opt.value === property && opt.lang);
        const showXref = property && allOptions.some(opt => opt.value === property && opt.xref);
        if (showLang) {
            newProperties[index].lang = this.state.lang;
        }
        else {
            delete newProperties[index].lang;
        }
        if (showXref) {
            newProperties[index].referenceType = "";
        }
        else {
            delete newProperties[index].referenceType;
        }
        this.setState({ properties: newProperties }, () => {
            this.processAvailableProps();
            this.isValidSpreadsheet();
        });
    };

    processAvailableProps = () => {
        const newAdditionalOptions = [...this.state.additionalOptions];
        newAdditionalOptions.forEach(ao => {
            const aoIRI = ao.value;
            const existsAsBoolean = this.props.loadInfo.some(li => li.iri === aoIRI
                && li.range && li.range.includes('http://www.w3.org/2001/XMLSchema#boolean'));
            const unsuitableDomain = this.props.loadInfo.some(li => li.iri === aoIRI
                && li.domain && li.domain.some(d => CONFIG_OPTIONS[this.props.configOptions].unsuitableDomains.includes(d)));
            const disabled = unsuitableDomain || (existsAsBoolean && this.state.properties.some(pr => pr.property === aoIRI));
            ao.disabled = disabled;
            return ao;
        });
        this.setState({ additionalOptions: newAdditionalOptions });
    };

    isValidSpreadsheet = () => {
        // Check there must be at least one label column, or for IRI the IRI column plus one other
        const byIRI = this.props.byIRI;
        const { properties } = this.state;
        const usedColumns = properties.filter(propertyItem => propertyItem.property);
        // Must be one an IRI column or a preferred label column in default language
        const isValidColumns = properties.some(propertyItem => (!byIRI && propertyItem.lang === this.state.lang
            && propertyItem.property === CONFIG_OPTIONS[this.props.configOptions].labelProp)
            || (byIRI && propertyItem.property === USE_IRI));
        // Check that reference types are selected and where using IRIs that any preferred label has a lang selected
        const invalidColumns = properties.some(propertyItem => propertyItem.referenceType === ""
            || (byIRI && propertyItem.property === CONFIG_OPTIONS[this.props.configOptions].labelProp && propertyItem.lang === this.state.lang)
        );
        const hasEnoughColumns = byIRI ? usedColumns.length > 1 : usedColumns.length > 0;
        const isValid = invalidColumns || !hasEnoughColumns ?
            false
            :
            isValidColumns && (this.state.isValidIRI || !this.props.includeOptions);
        // Filter out any nulls at this point
        const propsList = properties.map(prop => prop.property ? prop : {});
        this.props.onUpdateMapping(propsList,
            {
                baseIRI: this.state.baseIRI,
                lang: this.state.lang,
                ignoreHeaderRow: this.state.ignoreHeaderRow,
                ignoreInvalidValues: this.state.ignoreInvalidValues,
                useXL: this.state.useXL,
                codepage: this.state.codepage,
                title: this.state.title,
                removeSpaces: this.state.removeSpaces,
                replaceSpaces: this.state.replaceSpaces,
                removeSpecialCharacters: this.state.removeSpecialCharacters,
                replaceSpecialCharacters: this.state.replaceSpecialCharacters,
                minimiseEncoding: this.state.minimiseEncoding,
                prependText: this.state.prependText,
                appendText: this.state.appendLang ? this.state.appendText : ""
            },
            isValid);
    };

    changeLanguage = (index) => (lang) => {
        const newProperties = [...this.state.properties];
        newProperties[index].lang = lang;
        this.setState({ properties: newProperties }, () => this.isValidSpreadsheet());
    };

    changeXref = (index) => (referenceType) => {
        const newProperties = [...this.state.properties];
        // This might include references to other components or to labels (i.e. preferred)
        newProperties[index].referenceType = typeof referenceType === "string" && referenceType.startsWith("http") ?
            referenceType
            :
            referenceType - 1;
        this.setState({ properties: newProperties }, () => this.isValidSpreadsheet());
    };

    clearColumn = (index) => {
        const newProperties = [...this.state.properties];
        delete newProperties[index].lang;
        newProperties[index].property = null;
        this.setState({ properties: newProperties }, () => {
            this.processAvailableProps();
            this.isValidSpreadsheet();
        });
    };

    handleHeaderRowChange = checked => this.setState({ ignoreHeaderRow: checked }, () => this.isValidSpreadsheet());

    handleInvalidValuesChange = checked => this.setState({ ignoreInvalidValues: checked }, () => this.isValidSpreadsheet());

    handleXLchange = checked => this.setState({ useXL: checked }, () => this.isValidSpreadsheet());

    handleChangeRows = showRows => this.setState({ showRows });

    selectLanguage = (lang) => this.setState({ lang, appendText: lang }, () => this.isValidSpreadsheet());

    toggleAdvanceOptions = (showAdvancedOptions) => this.setState({ showAdvancedOptions });

    onUpdateIRI = () => {
        this.setState({ isValidIRI: false }, () => {
            this.isValidSpreadsheet();
        });
    };

    handleIRIChange = (iri, isValidIRI) => {
        this.setState({ baseIRI: iri, isValidIRI }, () => {
            this.isValidSpreadsheet();
        });
    };

    handleChange = event => {
        const t = event.target;
        const cleanText = sanitiseText(event.target.value);
        this.setState({ [t.name]: cleanText }, () => this.isValidSpreadsheet());
    };

    handleOptionChange = (event) => {
        const checked = event.target.checked;
        const property = event.target.name;
        this.setState({ [property]: checked }, () => this.isValidSpreadsheet());
    };

    handlePrependChange = (event) => {
        const checked = event.target.checked;
        this.setState({
            prependLabel: checked,
            prependText: checked ? "label" : ""
        }, () => this.isValidSpreadsheet());
    };

    handleIriSettingChange = (event) => {
        const checked = event.target.checked;
        const property = event.target.name;
        this.setState({ [property]: checked ? '-' : '' }, () => this.isValidSpreadsheet());
    };

    handleCodepageChange = (checked) => {
        this.setState({ codepage: checked ? 65001 : undefined }, async () => {
            await this.loadSpreadsheet();
            this.isValidSpreadsheet();
        });
    };

    onUpdatePrependLabel = (value) => this.setState({ prependText: value }, () => this.isValidSpreadsheet());

    render() {

        const { classes, iriFormat, iriType, onSlashHashChange, onFormatChange, project, initialSettings,
            primaryCounter, secondaryCounter, optionForXL, includeOptions, existingComponents, configOptions, byIRI, projectLang, onPrimaryChange, onSecondaryChange } = this.props;

        const { ss, columnsCount, properties, ignoreHeaderRow, showRows, title, additionalOptions, ignoreInvalidValues, menuOptions,
            lang, emptyCols, errors, rowsCount, isLoading, useXL, maxRows, showAdvancedOptions, xrefComponents, minimiseEncoding, codepage,
            removeSpaces, replaceSpaces, removeSpecialCharacters, replaceSpecialCharacters, prependText, appendLang, prependLabel } = this.state;
        const columns = Array.from({ length: columnsCount }, (v, k) => k + 1);
        const displayRows = Math.min(parseInt(showRows), rowsCount);

        const xrefColumns = columns.map(colIndex => ({
            value: colIndex,
            label: "Column " + colIndex,
            colIndex: colIndex - 1
        }));
        const xrefOptions = CONFIG_OPTIONS[configOptions].xrefOptions;
        const refOptions = [...xrefOptions, ...xrefComponents, ...xrefColumns];
        let allOptions = cloneDeep([...menuOptions, ...additionalOptions]);
        // If only a single label allowed and it has been used disabled that menu option
        if (CONFIG_OPTIONS[configOptions].singleLabel) {
            const labelUsed = properties.some(pr => pr.property === CONFIG_OPTIONS[configOptions].labelProp);
            if (labelUsed) {
                const labelOption = allOptions.find(ao => ao.value === CONFIG_OPTIONS[configOptions].labelProp);
                if (labelOption) labelOption.disabled = true;
            }
        }
        else if (byIRI) {
            if (projectLang.length === 1) {
                const labelOption = allOptions.find(ao => ao.value === CONFIG_OPTIONS[configOptions].labelProp);
                if (labelOption) labelOption.disabled = true;
            }
            const iriUsed = properties.some(pr => pr.property === USE_IRI);
            if (iriUsed) {
                const iriOption = allOptions.find(ao => ao.value === USE_IRI);
                if (iriOption) iriOption.disabled = true;
            }
        }

        const showPropWarnings = properties.filter(prop => prop.property && additionalOptions.find(ao => ao.isTemplateProp && ao.value === prop.property));
        const showPropWarningLabels = showPropWarnings.map(spw => additionalOptions.find(ao => ao.value === spw.property).label);

        if (errors && errors.length > 0) {
            return <div style={{ marginTop: 50 }}>
                <Typography align="center">
                    <ErrorIcon style={{ height: '50px', width: '50px', color: '#DD2C00' }} />
                </Typography>
                {errors.map((error, index) => <Typography data-test={"error-" + index} key={index} align="center" variant="body2">{error}</Typography>)}
            </div>;
        }

        if (isLoading) return <Loading offset="100px" />;

        if (rowsCount > maxRows) {
            return <div style={{ paddingTop: 20, paddingRight: 30, textAlign: 'center' }}>
                <Typography align="center" variant="body2">This spreadsheet is too large for your subscription.</Typography>
                <Typography data-test="max-sub-rows" align="center" variant="body2">The maximum number of rows permitted by this subscription is {maxRows}.</Typography>
                <Upgrade />
            </div>;
        }

        if (this.props.defaultLang === "und") {
            return <div data-test="no-support" style={{ paddingTop: 20, paddingRight: 30, width: '100%', textAlign: 'center' }}>
                <Typography align="center" variant="body2">Spreadsheet import is not currently supported for projects with no default language.</Typography>
            </div>;
        }

        return (
            <Grid container spacing={2}>
                {includeOptions ?
                    <React.Fragment>
                        <Grid item xs={6} md={4}>
                            <TitledPanel dataTest="input-language" title="Language" margin>
                                <ProjectLanguageSelector
                                    excludeNoLanguage={true}
                                    includeLabel={false}
                                    selectedLang={lang}
                                    onSelect={this.selectLanguage} />
                            </TitledPanel>
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <TitledTextField
                                dataTest="title"
                                name="title"
                                margin
                                title="Title"
                                autoFocus={true}
                                text={title}
                                placeholder="Enter title"
                                maxLength={MAX_TITLE_LENGTH}
                                lang={lang}
                                onChange={this.handleChange} />
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <BaseIRI
                                initialIRI={this.props.initialSettings.baseIRI || ""}
                                existingComponents={existingComponents}
                                project={project}
                                help={true}
                                onUpdateIRI={this.onUpdateIRI}
                                onValidatedIRI={this.handleIRIChange} />
                        </Grid>
                        <Grid item xs={4}>
                            <SlashHash margin={false} onChange={onSlashHashChange} iriType={iriType} />
                        </Grid>
                        <Grid item xs={4}>
                            <IRIgeneration
                                margin={false}
                                allowCounter={true}
                                onChange={onFormatChange}
                                primaryCounter={primaryCounter}
                                handlePrimaryChange={onPrimaryChange}
                                handleSecondaryChange={onSecondaryChange}
                                secondaryCounter={secondaryCounter}
                                showSecondaryCounter={optionForXL}
                                showCounters={iriFormat === "Counter"}
                                iriFormat={iriFormat} />
                        </Grid>
                        {iriFormat === "Label" &&
                            <Grid item xs={12}>
                                <TitledPanel
                                    dataTest="iri-options"
                                    title="Advanced IRI options"
                                    actions={
                                        <div style={{ marginTop: 4 }}>
                                            <Tooltip title="Toggle the advanced IRI options view" className={classes.smallIconButton}>
                                                <IconButton data-test="advanced-options-toggle" size="small" onClick={() => this.toggleAdvanceOptions(!showAdvancedOptions)}>
                                                    {showAdvancedOptions ?
                                                        <VisibilityOffIcon />
                                                        :
                                                        <VisibilityIcon />
                                                    }
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    }>
                                    {showAdvancedOptions &&
                                        <Grid container>
                                            <Grid item xs={6} md={4}>
                                                <FormControlLabel
                                                    data-test={"option-removeSpaces"}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            color="primary"
                                                            name={"removeSpaces"}
                                                            className={classes.checked}
                                                            checked={removeSpaces}
                                                            onClick={(ev) => this.handleOptionChange(ev)} />}
                                                    label="Remove Spaces"
                                                    classes={{ label: classes.formLabelInline }}
                                                />
                                            </Grid>
                                            <Grid item xs={6} md={8}>
                                                <FormControlLabel
                                                    data-test={"option-replaceSpaces"}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            color="primary"
                                                            name={"replaceSpaces"}
                                                            disabled={removeSpaces}
                                                            className={classes.checked}
                                                            checked={replaceSpaces === "-"}
                                                            onClick={(ev) => this.handleIriSettingChange(ev)} />}
                                                    label="Replace Spaces (With Hyphens)"
                                                    classes={{ label: classes.formLabelInline }}
                                                />
                                            </Grid>
                                            <Grid item xs={6} md={4}>
                                                <FormControlLabel
                                                    data-test={"option-removeSpecialCharacters"}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            color="primary"
                                                            name={"removeSpecialCharacters"}
                                                            className={classes.checked}
                                                            checked={removeSpecialCharacters}
                                                            onClick={(ev) => this.handleOptionChange(ev)} />}
                                                    label="Remove Special Characters"
                                                    classes={{ label: classes.formLabelInline }}
                                                />
                                            </Grid>
                                            <Grid item xs={6} md={8}>
                                                <FormControlLabel
                                                    data-test={"option-replaceSpecialCharacters"}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            color="primary"
                                                            name={"replaceSpecialCharacters"}
                                                            disabled={removeSpecialCharacters}
                                                            className={classes.checked}
                                                            checked={replaceSpecialCharacters === "-"}
                                                            onClick={(ev) => this.handleIriSettingChange(ev)} />}
                                                    label="Replace Special Characters (With Hyphens)"
                                                    classes={{ label: classes.formLabelInline }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControlLabel
                                                    data-test={"option-minimiseEncoding"}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            color="primary"
                                                            name={"minimiseEncoding"}
                                                            className={classes.checked}
                                                            checked={minimiseEncoding || false}
                                                            onClick={(ev) => this.handleOptionChange(ev)} />}
                                                    label="Minimise IRI Encoding"
                                                    classes={{ label: classes.formLabelInline }}
                                                />
                                            </Grid>
                                            {useXL &&
                                                <React.Fragment>
                                                    <Grid item xs={12}>
                                                        <div style={{ marginTop: 12 }}>
                                                            <Typography variant="caption">For Labels Only</Typography>
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={6} md={4}>
                                                        <FormControlLabel
                                                            data-test={"option-prependLabel"}
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    color="primary"
                                                                    name={"prependLabel"}
                                                                    className={classes.checked}
                                                                    checked={prependLabel}
                                                                    onClick={(ev) => this.handlePrependChange(ev)} />}
                                                            label="Prepend Label IRI"
                                                            classes={{ label: classes.formLabelInline }}
                                                        />
                                                        {prependLabel &&
                                                            <div style={{ marginLeft: 24, marginTop: -12, maxWidth: 200 }}>
                                                                <LiteralConfirmationField
                                                                    displayDatatype={false}
                                                                    margin="dense"
                                                                    maxLength={10}
                                                                    allowSpaces={false}
                                                                    dataTest="prepend-label"
                                                                    text={prependText}
                                                                    datatype="http://www.w3.org/2001/XMLSchema#string"
                                                                    canBeDeleted={false}
                                                                    onUpdate={this.onUpdatePrependLabel} />
                                                            </div>
                                                        }
                                                    </Grid>
                                                    <Grid item xs={6} md={8}>
                                                        <FormControlLabel
                                                            data-test={"option-appendLanguageWithLanguageCode"}
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    color="primary"
                                                                    name={"appendLang"}
                                                                    className={classes.checked}
                                                                    checked={appendLang}
                                                                    onClick={(ev) => this.handleOptionChange(ev)} />}
                                                            label="Append Language Code to Label IRI"
                                                            classes={{ label: classes.formLabelInline }}
                                                        />
                                                    </Grid>
                                                </React.Fragment>
                                            }
                                        </Grid>
                                    }
                                </TitledPanel>
                            </Grid>
                        }
                    </React.Fragment>
                    :
                    <Grid item xs={12}>
                    </Grid>
                }
                <Grid item xs={12}>
                    <div style={{ backgroundColor: 'white', overflow: 'auto', padding: 8, borderRadius: 4 }}>
                        <div style={
                            { float: 'right', width: 110, marginBottom: 10 }}>
                            <MenuSelector
                                dataTest="rows-selector"
                                hintPrefix={false}
                                label="Preview Rows"
                                selected={showRows}
                                handleChange={this.handleChangeRows}
                                options={ROW_PREVIEW_COUNTS} />
                        </div>
                        <Typography className={classes.mainHeading}>Map Your Data</Typography>
                        {byIRI ?
                            <Typography variant="body2">There must be one IRI column and at least one other column selected.</Typography>
                            :
                            <Typography variant="body2">There must be at least one label column using the selected language.</Typography>
                        }
                        <Typography paragraph variant="body2">If the preview doesn't quite look right try the 'Adjust Encoding' option.</Typography>
                        {showPropWarningLabels && showPropWarningLabels.length > 0 &&
                            <div style={{ marginTop: 12, marginBottom: 12 }}>
                                <Notification error={false} text={"You are currently selecting properties from templates: " + showPropWarningLabels.join(", ") + ". If you want these properties to display please ensure you add any required templates after import."} dataTest="warning-on-props" />
                            </div>
                        }
                        {rowsCount > 1 &&
                            <div style={{ float: 'left' }}>
                                <LabelledSwitch
                                    dataTest="ignore-header-row"
                                    label="Ignore Header Row"
                                    checked={ignoreHeaderRow}
                                    handleChange={this.handleHeaderRowChange}
                                />
                            </div>
                        }
                        <div style={{ float: 'left' }}>
                            <LabelledSwitch
                                dataTest="ignore-invalid-values"
                                label="Ignore Invalid Values"
                                checked={ignoreInvalidValues}
                                handleChange={this.handleInvalidValuesChange}
                            />
                        </div>
                        {optionForXL && !byIRI &&
                            <div style={{ float: 'left' }}>
                                <LabelledSwitch
                                    dataTest="use-xl"
                                    label="Use XL for Labels"
                                    checked={useXL}
                                    handleChange={this.handleXLchange}
                                />
                            </div>
                        }
                        <div style={{ float: 'left' }}>
                            <LabelledSwitch
                                dataTest="use-encoding"
                                label="Adjust Encoding"
                                checked={codepage !== undefined}
                                handleChange={this.handleCodepageChange}
                            />
                        </div>
                    </div>
                    <div style={{ backgroundColor: 'white', overflow: 'auto', paddingLeft: 8, paddingRight: 8, paddingBottom: 8 }}>
                        <Table data-test="spreadsheet-viewer" style={{ maxWidth: '100%', borderBottom: 'solid 1px #e0e0e0' }}>
                            <TableBody>
                                <TableRow data-test="column-row" className={classes.shadedRow}>
                                    {columns.map((cell, cellIndex) => {
                                        const propertyItem = properties[cellIndex];
                                        const property = properties[cellIndex].property;
                                        const showXref = property && allOptions.some(opt => opt.value === property && opt.xref);
                                        return <TableCell
                                            data-test={"column-" + cellIndex}
                                            key={cellIndex}
                                            className={classes.tableCell}
                                            style={cellIndex < columns.length - 1 ?
                                                {
                                                    paddingLeft: 8,
                                                    borderRight: 'solid 1px #c5c5c5',
                                                    backgroundColor: propertyItem.property ?
                                                        showXref && propertyItem.referenceType === "" ?
                                                            "#CA4F0C"
                                                            :
                                                            "#84BC01"
                                                        :
                                                        "#c0c0c0"
                                                }
                                                :
                                                {
                                                    paddingLeft: 8,
                                                    backgroundColor: propertyItem.property ?
                                                        showXref && propertyItem.referenceType === "" ?
                                                            "#CA4F0C"
                                                            :
                                                            "#84BC01"
                                                        :
                                                        "#c0c0c0"
                                                }}>
                                            <Typography className={classes.subHeading} style={{
                                                color: propertyItem.property ?
                                                    'white'
                                                    :
                                                    '#13294B',
                                                height: 30,
                                                letterSpacing: 0.5,
                                                minWidth: 150
                                            }}>
                                                <span style={{ display: 'inline-block', paddingTop: 6 }}>Column {cellIndex + 1}</span>
                                                {propertyItem.property &&
                                                    <IconButton data-test="clear-column"
                                                        className={classes.smallIconButton}
                                                        size="small"
                                                        style={{ float: 'right', color: 'white' }}
                                                        onClick={() => this.clearColumn(cellIndex)}>
                                                        <ClearIcon fontSize="small" />
                                                    </IconButton>
                                                }
                                            </Typography>
                                        </TableCell>;
                                    }
                                    )}
                                </TableRow>
                                <TableRow className={classes.shadedRow}>
                                    {columns.map((cell, cellIndex) => {
                                        const property = properties[cellIndex].property;
                                        const referenceType = properties[cellIndex].referenceType;
                                        const selectedLang = properties[cellIndex].lang;
                                        const aoData = additionalOptions.find(ao => ao.value === property);
                                        const hasRange = aoData && aoData.range;
                                        const showLang = property && allOptions.some(opt => opt.value === property && opt.lang);
                                        const showXref = property && allOptions.some(opt => opt.value === property && opt.xref);
                                        const xrefOtherComponents = property && allOptions.some(opt => opt.value === property && opt.xrefOtherComponents);
                                        return <TableCell
                                            data-test={"selector-" + cellIndex}
                                            key={cellIndex}
                                            className={classes.tableCell}
                                            style={cellIndex < columns.length - 1 ?
                                                {
                                                    borderRight: 'solid 1px #c5c5c5'
                                                }
                                                :
                                                {}}>
                                            {emptyCols[cellIndex] ?
                                                <div style={{ textAlign: 'center', marginTop: 8, height: '100%' }}>
                                                    <Typography variant="body2" style={{ color: '#767676' }}>Empty Column</Typography>
                                                </div>
                                                :
                                                <div style={{ flex: '1' }}>
                                                    <MenuSelector
                                                        key={property ? property + "-" + cellIndex : "default"}
                                                        dataTest="property-selector"
                                                        selected={property || ""}
                                                        handleChange={this.handleSelectProperty(cellIndex)}
                                                        hintWord="property"
                                                        showValues={true}
                                                        options={allOptions} />
                                                    <div style={{ marginTop: 12, height: 70 }}>
                                                        {showLang &&
                                                            <ProjectLanguageSelector
                                                                multiple
                                                                excludeNoLanguage={true}
                                                                excludeLangs={byIRI && property === CONFIG_OPTIONS[configOptions].labelProp ? [lang] : []}
                                                                includeLabel={true}
                                                                selectedLang={byIRI && property === CONFIG_OPTIONS[configOptions].labelProp ?
                                                                    ""
                                                                    :
                                                                    selectedLang || lang}
                                                                onSelect={this.changeLanguage(cellIndex)} />}
                                                        {showXref &&
                                                            <React.Fragment>
                                                                <MenuSelector
                                                                    key={"default"}
                                                                    dataTest="xref-selector"
                                                                    selected={typeof referenceType === "string" ?
                                                                        referenceType
                                                                        :
                                                                        (referenceType + 1).toString()
                                                                    }
                                                                    label="Cross-reference"
                                                                    handleChange={this.changeXref(cellIndex)}
                                                                    hintWord="cross-reference"
                                                                    options={refOptions.filter(option => option.colIndex !== cellIndex
                                                                        && (!initialSettings || !initialSettings.baseIRI || option.value !== initialSettings.baseIRI)
                                                                        && !(property === "http://www.w3.org/2004/02/skos/core#related" && option.component)
                                                                        && !(xrefOtherComponents && !option.component)
                                                                    )} />
                                                            </React.Fragment>
                                                        }
                                                    </div>
                                                    {showXref && hasRange && typeof referenceType === "string" && referenceType !== CONFIG_OPTIONS[this.props.configOptions].labelProp && <div>
                                                        <Notification dataTest="range-info" text="Targets must have a suitable class." />
                                                    </div>}
                                                </div>
                                            }
                                        </TableCell>;
                                    }
                                    )}
                                </TableRow>
                                <TableRow data-test="preview-header" className={classes.shadedRow}>
                                    <TableCell colSpan={columns.length} className={classes.tableCell} style={{ backgroundColor: '#13294B' }}>
                                        <Typography className={classes.subHeading} style={{ color: 'white' }}>
                                            <span>Data Preview</span>
                                            <span> (Showing {displayRows} of {rowsCount} rows, {columnsCount} columns)</span>
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                                {ss.map((row, rowIndex) => {
                                    return rowIndex <= displayRows ?
                                        <TableRow
                                            data-test={rowIndex === 1 && ignoreHeaderRow ? "ignore-row" : "preview-row"}
                                            key={"row-" + rowIndex}
                                            classes={{ root: classes.stripedRow }}>
                                            {row.map((cell, cellIndex) => {
                                                const property = properties[cellIndex].property;
                                                const selectedLang = properties[cellIndex].lang;
                                                const showLang = property && allOptions.some(opt => opt.value === property && opt.lang);
                                                const rtl = showLang && isRTL(selectedLang);
                                                return <TableCell
                                                    key={cellIndex}
                                                    className={classes.tableCell}
                                                    style={cellIndex < columns.length - 1 ?
                                                        {
                                                            borderRight: 'solid 1px #c5c5c5',
                                                            ...(rtl) && { textAlign: 'right' },
                                                            ...(rowIndex === 1 && ignoreHeaderRow) && { backgroundColor: "#d0d0d0" }
                                                        }
                                                        :
                                                        {
                                                            ...(rtl) && { textAlign: 'right' },
                                                            ...(rowIndex === 1 && ignoreHeaderRow) && { backgroundColor: "#d0d0d0" }
                                                        }
                                                    }>
                                                    <Typography
                                                        dir={rtl ? "rtl" : null}
                                                        lang={selectedLang}
                                                        className={classes.tableCellText}>
                                                        {
                                                            cell ?
                                                                cell.split("\n").map((line, index) => <span key={index} style={{
                                                                    display: 'block',
                                                                    ...(rowIndex === 1 && ignoreHeaderRow) && { fontWeight: 500 }
                                                                }}>{line}</span>
                                                                )
                                                                :
                                                                <span>&#160;</span>
                                                        }
                                                    </Typography>
                                                </TableCell>;
                                            }
                                            )}
                                        </TableRow>
                                        :
                                        null;
                                }
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </Grid>
            </Grid>
        );
    }
}

SheetPreview.defaultProps = {
    optionForXL: false,
    includeOptions: true,
    byIRI: false,
    maxRows: MAX_ROWS
};

SheetPreview.propTypes = {
    classes: PropTypes.object,
    lang: PropTypes.string,
    maxRows: PropTypes.number,
    defaultLang: PropTypes.string.isRequired,
    onUpdateMapping: PropTypes.func.isRequired,
    configOptions: PropTypes.string.isRequired,
    iriType: PropTypes.string.isRequired,
    iriFormat: PropTypes.string.isRequired,
    project: PropTypes.string,
    existingComponents: PropTypes.array,
    initialSettings: PropTypes.object,
    file: PropTypes.object,
    loadInfo: PropTypes.array,
    projectLang: PropTypes.array,
    byIRI: PropTypes.bool,
    limits: PropTypes.string,
    includeOptions: PropTypes.bool,
    optionForXL: PropTypes.bool,
    onSlashHashChange: PropTypes.func.isRequired,
    onFormatChange: PropTypes.func.isRequired,
    onPrimaryChange: PropTypes.func.isRequired,
    onSecondaryChange: PropTypes.func.isRequired
};

export default withStyles(styles)(SheetPreview);